<template>
    <div class="dashboard-page" v-loading="state.loading">
        <div class="dashboard-head">
            <div>
                <div class="dashboard-title">{{ text.title }}</div>
                <div class="dashboard-subtitle">{{ text.subtitle }}</div>
            </div>
            <el-button type="primary" plain @click="loadData">{{ text.refresh }}</el-button>
        </div>

        <div class="metric-grid">
            <div v-for="item in metricCards" :key="item.key" class="metric-card">
                <div class="metric-label">{{ item.label }}</div>
                <div class="metric-value">{{ item.value }}</div>
                <div class="metric-note">{{ item.note }}</div>
            </div>
        </div>

        <el-row :gutter="16" class="panel-row">
            <el-col :xs="24" :lg="12">
                <el-card shadow="never" class="dashboard-panel">
                    <template #header>{{ text.orderTrend }}</template>
                    <div class="trend-list">
                        <div v-for="item in state.data.orderTrend" :key="item.date" class="trend-item">
                            <span class="trend-date">{{ item.date }}</span>
                            <div class="trend-bars">
                                <div class="trend-bar order" :style="{ width: percent(item.orderCount, maxOrderCount) }"></div>
                                <div class="trend-bar revenue" :style="{ width: percent(item.revenueAmount, maxRevenueAmount) }"></div>
                            </div>
                            <span class="trend-value">{{ item.orderCount || 0 }}{{ text.orderUnit }} / {{ money(item.revenueAmount) }}</span>
                        </div>
                    </div>
                </el-card>
            </el-col>
            <el-col :xs="24" :lg="12">
                <el-card shadow="never" class="dashboard-panel">
                    <template #header>{{ text.invokeTrend }}</template>
                    <div class="trend-list">
                        <div v-for="item in state.data.invokeTrend" :key="item.date" class="trend-item">
                            <span class="trend-date">{{ item.date }}</span>
                            <div class="trend-bars stacked">
                                <div class="trend-bar success" :style="{ width: percent(item.successCount, maxInvokeCount) }"></div>
                                <div class="trend-bar fail" :style="{ width: percent(item.failCount, maxInvokeCount) }"></div>
                            </div>
                            <span class="trend-value">{{ text.success }} {{ item.successCount || 0 }} / {{ text.fail }} {{ item.failCount || 0 }}</span>
                        </div>
                    </div>
                </el-card>
            </el-col>
        </el-row>

        <el-row :gutter="16" class="panel-row">
            <el-col :xs="24" :lg="8">
                <el-card shadow="never" class="dashboard-panel rank-panel">
                    <template #header>{{ text.productRank }}</template>
                    <div v-if="state.data.productRank.length" class="rank-list">
                        <div v-for="(item, index) in state.data.productRank" :key="item.productType || index" class="rank-item">
                            <span class="rank-no">{{ index + 1 }}</span>
                            <span class="rank-name">{{ item.productName || item.productType || '--' }}</span>
                            <span class="rank-value">{{ item.orderCount || 0 }}{{ text.orderUnit }}</span>
                        </div>
                    </div>
                    <el-empty v-else :description="text.noData" :image-size="72" />
                </el-card>
            </el-col>
            <el-col :xs="24" :lg="8">
                <el-card shadow="never" class="dashboard-panel rank-panel">
                    <template #header>{{ text.vendorRank }}</template>
                    <div v-if="state.data.vendorRank.length" class="rank-list">
                        <div v-for="(item, index) in state.data.vendorRank" :key="item.thirdPartyName || index" class="rank-item">
                            <span class="rank-no">{{ index + 1 }}</span>
                            <span class="rank-name">{{ item.thirdPartyName || '--' }}</span>
                            <span class="rank-value">{{ item.invokeCount || 0 }}{{ text.times }}</span>
                        </div>
                    </div>
                    <el-empty v-else :description="text.noData" :image-size="72" />
                </el-card>
            </el-col>
            <el-col :xs="24" :lg="8">
                <el-card shadow="never" class="dashboard-panel rank-panel">
                    <template #header>{{ text.failRank }}</template>
                    <div v-if="state.data.vendorRank.length" class="rank-list">
                        <div v-for="(item, index) in state.data.vendorRank" :key="item.thirdPartyName + '-fail' || index" class="rank-item">
                            <span class="rank-no danger">{{ index + 1 }}</span>
                            <span class="rank-name">{{ item.thirdPartyName || '--' }}</span>
                            <span class="rank-value danger">{{ item.failRate || '0%' }}</span>
                        </div>
                    </div>
                    <el-empty v-else :description="text.noData" :image-size="72" />
                </el-card>
            </el-col>
        </el-row>

        <el-card shadow="never" class="dashboard-panel">
            <template #header>{{ text.latestFailures }}</template>
            <el-table :data="state.data.latestFailures" border stripe height="260">
                <el-table-column prop="requestTime" :label="text.requestTime" min-width="170" />
                <el-table-column prop="productType" :label="text.productType" min-width="100" />
                <el-table-column prop="thirdPartyName" :label="text.vendor" min-width="120" />
                <el-table-column prop="apiName" :label="text.api" min-width="210" show-overflow-tooltip />
                <el-table-column prop="vin" label="VIN" min-width="170" />
                <el-table-column prop="failReason" :label="text.failReason" min-width="220" show-overflow-tooltip />
            </el-table>
        </el-card>
    </div>
</template>

<script setup>
import { computed, onMounted, reactive } from "vue";
import { ElMessage } from "element-plus";
import { getDashboardOverviewFun } from "@/api/dashboard";

const text = {
    title: "\u6570\u636e\u770b\u677f",
    subtitle: "\u5173\u6ce8\u8ba2\u5355\u3001\u6536\u5165\u3001\u7b2c\u4e09\u65b9\u8c03\u7528\u548c\u5f02\u5e38\u60c5\u51b5",
    refresh: "\u5237\u65b0",
    todayOrder: "\u4eca\u65e5\u8ba2\u5355",
    todayRevenue: "\u4eca\u65e5\u6536\u5165",
    thirdPartyCount: "\u4eca\u65e5\u7b2c\u4e09\u65b9\u8c03\u7528",
    thirdPartyFail: "\u4eca\u65e5\u5931\u8d25\u8c03\u7528",
    productCount: "\u4e0a\u67b6\u4ea7\u54c1",
    pendingReport: "\u672a\u51fa\u62a5\u544a\u8bb0\u5f55",
    orderTrend: "\u8fd1 7 \u5929\u8ba2\u5355 / \u6536\u5165\u8d8b\u52bf",
    invokeTrend: "\u8fd1 7 \u5929\u7b2c\u4e09\u65b9\u8c03\u7528\u8d8b\u52bf",
    productRank: "\u4ea7\u54c1\u9500\u91cf\u6392\u884c",
    vendorRank: "\u5382\u5546\u8c03\u7528\u6392\u884c",
    failRank: "\u5382\u5546\u5931\u8d25\u7387",
    latestFailures: "\u6700\u65b0\u5931\u8d25\u8c03\u7528",
    requestTime: "\u8bf7\u6c42\u65f6\u95f4",
    productType: "\u4e1a\u52a1\u7c7b\u578b",
    vendor: "\u7b2c\u4e09\u65b9\u5382\u5546",
    api: "\u63a5\u53e3",
    failReason: "\u5931\u8d25\u539f\u56e0",
    noData: "\u6682\u65e0\u6570\u636e",
    orderUnit: "\u5355",
    times: "\u6b21",
    success: "\u6210\u529f",
    fail: "\u5931\u8d25",
    yuan: "\u5143",
    loadFail: "\u770b\u677f\u6570\u636e\u52a0\u8f7d\u5931\u8d25",
};

const emptyData = () => ({
    summary: {},
    orderTrend: [],
    invokeTrend: [],
    productRank: [],
    vendorRank: [],
    latestFailures: [],
});

const state = reactive({
    loading: false,
    data: emptyData(),
});

const metricCards = computed(() => {
    const summary = state.data.summary || {};
    return [
        { key: "todayOrderCount", label: text.todayOrder, value: (summary.todayOrderCount || 0) + text.orderUnit, note: text.orderTrend },
        { key: "todayRevenueAmount", label: text.todayRevenue, value: money(summary.todayRevenueAmount), note: text.todayRevenue },
        { key: "todayThirdPartyCount", label: text.thirdPartyCount, value: (summary.todayThirdPartyCount || 0) + text.times, note: text.invokeTrend },
        { key: "todayThirdPartyFailCount", label: text.thirdPartyFail, value: (summary.todayThirdPartyFailCount || 0) + text.times, note: text.latestFailures },
        { key: "productCount", label: text.productCount, value: summary.productCount || 0, note: text.productRank },
        { key: "pendingReportCount", label: text.pendingReport, value: summary.pendingReportCount || 0, note: text.failReason },
    ];
});

const maxOrderCount = computed(() => Math.max(1, ...state.data.orderTrend.map((item) => Number(item.orderCount || 0))));
const maxRevenueAmount = computed(() => Math.max(1, ...state.data.orderTrend.map((item) => Number(item.revenueAmount || 0))));
const maxInvokeCount = computed(() => Math.max(1, ...state.data.invokeTrend.map((item) => Number(item.successCount || 0) + Number(item.failCount || 0))));

function loadData() {
    state.loading = true;
    getDashboardOverviewFun()
        .then((res) => {
            if (res.code === 200) {
                state.data = Object.assign(emptyData(), res.data || {});
            } else {
                ElMessage.error(res.msg || text.loadFail);
            }
        })
        .catch(() => ElMessage.error(text.loadFail))
        .finally(() => {
            state.loading = false;
        });
}

function money(value) {
    return "\u00a5" + (Number(value || 0) / 100).toFixed(2);
}

function percent(value, max) {
    const result = Math.max(4, Math.round((Number(value || 0) / Number(max || 1)) * 100));
    return result + "%";
}

onMounted(loadData);
</script>

<style scoped lang="scss">
.dashboard-page {
    min-height: 100%;
}

.dashboard-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;
}

.dashboard-title {
    font-size: 22px;
    font-weight: 700;
    color: #1f2937;
}

.dashboard-subtitle {
    margin-top: 4px;
    font-size: 13px;
    color: #8a94a6;
}

.metric-grid {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 14px;
}

.metric-card {
    min-height: 104px;
    padding: 16px;
    border: 1px solid #e5e7eb;
    background: #fff;
    border-radius: 6px;
    box-sizing: border-box;
}

.metric-label {
    font-size: 13px;
    color: #6b7280;
}

.metric-value {
    margin-top: 12px;
    font-size: 26px;
    font-weight: 700;
    color: #111827;
}

.metric-note {
    margin-top: 8px;
    font-size: 12px;
    color: #9ca3af;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.panel-row {
    margin-top: 16px;
}

.dashboard-panel {
    border-radius: 6px;

    :deep(.el-card__header) {
        padding: 14px 16px;
        font-weight: 600;
        color: #1f2937;
    }

    :deep(.el-card__body) {
        padding: 16px;
    }
}

.trend-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.trend-item {
    display: grid;
    grid-template-columns: 48px minmax(0, 1fr) 150px;
    gap: 12px;
    align-items: center;
}

.trend-date,
.trend-value {
    font-size: 12px;
    color: #6b7280;
}

.trend-value {
    text-align: right;
}

.trend-bars {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.trend-bars.stacked {
    flex-direction: row;
    gap: 6px;
}

.trend-bar {
    height: 8px;
    min-width: 4px;
    border-radius: 999px;
}

.trend-bar.order,
.trend-bar.success {
    background: var(--admin-theme-color, #2d3a4b);
}

.trend-bar.revenue {
    background: #67c23a;
}

.trend-bar.fail {
    background: #f56c6c;
}

.rank-panel {
    min-height: 280px;
}

.rank-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.rank-item {
    display: grid;
    grid-template-columns: 26px minmax(0, 1fr) auto;
    gap: 10px;
    align-items: center;
}

.rank-no {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: var(--admin-theme-light, rgba(45, 58, 75, 0.1));
    color: var(--admin-theme-color, #2d3a4b);
    font-size: 12px;
    line-height: 22px;
    text-align: center;
}

.rank-no.danger {
    background: #fef2f2;
    color: #dc2626;
}

.rank-name {
    color: #374151;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.rank-value {
    color: #111827;
    font-weight: 600;
}

.rank-value.danger {
    color: #dc2626;
}

@media (max-width: 1280px) {
    .metric-grid {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }
}
</style>
